import naja from 'naja';
import netteForms from 'nette-forms';
import * as bootstrap from 'bootstrap';
import Select from 'tom-select';
import {Datepicker as VanillaDatepickerCore} from 'vanillajs-datepicker';
import czechDatepickerLocale from 'vanillajs-datepicker/locales/cs';
import {
  AutosubmitPlugin,
  CheckboxPlugin,
  ConfirmPlugin,
  createDatagrids,
  DatepickerPlugin,
  EditablePlugin,
  InlinePlugin,
  ItemDetailPlugin,
  NetteFormsPlugin,
  SelectpickerPlugin,
  SortableJS,
  SortablePlugin,
  TomSelect,
  TreeViewPlugin,
  VanillaDatepicker,
} from '@contributte/datagrid/assets/index.ts';
import {NajaAjax} from '@contributte/datagrid/assets/ajax/index.ts';

Object.assign(VanillaDatepickerCore.locales, czechDatepickerLocale);

function parseDatepickerBoolean(value, fallback = false) {
  if (value === undefined) return fallback;
  const normalized = value.trim().toLowerCase();
  if (normalized === '' || normalized === 'true' || normalized === '1') return true;
  if (normalized === 'false' || normalized === '0') return false;
  return fallback;
}

function datagridDatepickerOrientation(value) {
  const orientation = value?.trim() || 'auto';

  // Contributte's FilterDate still emits the legacy Bootstrap instruction
  // "bottom" for every grid filter. Keeping it fixed makes the Vanilla
  // Datepicker run below the viewport when a filter is near its lower edge.
  // Restore adaptive vertical placement while preserving genuinely custom
  // compound orientations supplied by the application.
  return orientation === 'bottom' ? 'auto' : orientation;
}

function datagridDatepickerOptions(input) {
  return {
    autohide: parseDatepickerBoolean(input.dataset.dateAutoclose),
    buttonClass: 'btn',
    format: input.dataset.dateFormat || 'd. m. yyyy',
    language: 'cs',
    orientation: datagridDatepickerOrientation(input.dataset.dateOrientation),
    todayHighlight: parseDatepickerBoolean(input.dataset.dateTodayHighlight),
    weekStart: 1,
  };
}

function constrainDatepickerToViewport(datepicker) {
  const picker = datepicker?.picker?.element;
  if (!(picker instanceof HTMLElement) || !picker.classList.contains('active')) return;

  const viewport = window.visualViewport;
  const viewportLeft = viewport?.offsetLeft || 0;
  const viewportTop = viewport?.offsetTop || 0;
  const viewportRight = viewportLeft + (viewport?.width || window.innerWidth);
  const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
  const navbarBottom = document.querySelector('.navbar-fixed-top')?.getBoundingClientRect().bottom || viewportTop;
  const minimumLeft = viewportLeft + 8;
  const minimumTop = Math.max(viewportTop + 8, navbarBottom + 8);
  const maximumRight = viewportRight - 8;
  const maximumBottom = viewportBottom - 8;
  const rect = picker.getBoundingClientRect();
  let horizontalShift = 0;
  let verticalShift = 0;

  if (rect.right > maximumRight) horizontalShift = maximumRight - rect.right;
  if (rect.left + horizontalShift < minimumLeft) horizontalShift = minimumLeft - rect.left;
  if (rect.bottom > maximumBottom) verticalShift = maximumBottom - rect.bottom;
  if (rect.top + verticalShift < minimumTop) verticalShift = minimumTop - rect.top;

  if (horizontalShift) {
    const currentLeft = Number.parseFloat(picker.style.left);
    if (Number.isFinite(currentLeft)) picker.style.left = `${currentLeft + horizontalShift}px`;
  }
  if (verticalShift) {
    const currentTop = Number.parseFloat(picker.style.top);
    if (Number.isFinite(currentTop)) picker.style.top = `${currentTop + verticalShift}px`;
  }
}

function initializeDatepickerViewportGuard() {
  document.addEventListener('show', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-provide="datepicker"]')) return;
    window.requestAnimationFrame(() => constrainDatepickerToViewport(event.detail?.datepicker || input.datepicker));
  });
}

const initializedForms = new WeakSet();
const initializeForm = netteForms.initForm.bind(netteForms);

netteForms.initForm = (form) => {
  if (initializedForms.has(form)) return;
  initializeForm(form);
  initializedForms.add(form);
};

window.Nette = netteForms;
window.naja = naja;
window.bootstrap = bootstrap;

const loadingFeedback = (() => {
  const showDelay = 120;
  const minimumVisibleTime = 320;
  const activities = new Map();
  let sequence = 0;
  let showTimer = null;
  let hideTimer = null;
  let visibleSince = 0;

  function indicator() {
    return document.getElementById('fido-loading-feedback');
  }

  function triggerElement(value) {
    if (!(value instanceof Element) || !value.isConnected) return null;
    if (value instanceof HTMLFormElement) {
      const focused = document.activeElement;
      if (focused instanceof Element && focused.form === value) return focused;
      return value.querySelector('button[type="submit"], input[type="submit"], .btn');
    }
    return value;
  }

  function decorateTrigger(value, active) {
    const trigger = triggerElement(value);
    if (!trigger) return;
    const canShowSpinner = trigger.matches('button, .btn, .page-link, input[type="submit"], input[type="image"]');
    if (canShowSpinner) trigger.classList.toggle('fido-loading-trigger', active);
    if (active) trigger.setAttribute('aria-busy', 'true');
    else trigger.removeAttribute('aria-busy');
  }

  function refreshMessage() {
    const message = [...activities.values()].at(-1)?.message || 'Pracuji…';
    const label = indicator()?.querySelector('.fido-loading-label');
    if (label) label.textContent = message;
  }

  function reveal() {
    showTimer = null;
    if (!activities.size) return;
    clearTimeout(hideTimer);
    hideTimer = null;
    const element = indicator();
    if (!element) return;
    refreshMessage();
    activities.forEach(activity => decorateTrigger(activity.trigger, true));
    element.classList.add('is-visible');
    element.setAttribute('aria-hidden', 'false');
    document.body.dataset.fidoLoading = 'true';
    document.body.setAttribute('aria-busy', 'true');
    visibleSince ||= performance.now();
  }

  function conceal() {
    hideTimer = null;
    if (activities.size) return;
    const element = indicator();
    element?.classList.remove('is-visible');
    element?.setAttribute('aria-hidden', 'true');
    document.body?.removeAttribute('data-fido-loading');
    document.body?.removeAttribute('aria-busy');
    visibleSince = 0;
  }

  function begin({trigger = null, message = 'Pracuji…', immediate = false} = {}) {
    const token = ++sequence;
    activities.set(token, {trigger: triggerElement(trigger), message});
    clearTimeout(hideTimer);
    hideTimer = null;
    if (visibleSince) {
      refreshMessage();
      decorateTrigger(trigger, true);
    } else if (immediate) {
      clearTimeout(showTimer);
      reveal();
    } else if (!showTimer) {
      showTimer = window.setTimeout(reveal, showDelay);
    }
    return token;
  }

  function end(token) {
    const activity = activities.get(token);
    if (!activity) return;
    activities.delete(token);
    if (![...activities.values()].some(candidate => candidate.trigger === activity.trigger)) {
      decorateTrigger(activity.trigger, false);
    }
    if (activities.size) {
      refreshMessage();
      return;
    }
    clearTimeout(showTimer);
    showTimer = null;
    if (!visibleSince) return;
    const remaining = Math.max(0, minimumVisibleTime - (performance.now() - visibleSince));
    hideTimer = window.setTimeout(conceal, remaining);
  }

  function reset() {
    clearTimeout(showTimer);
    clearTimeout(hideTimer);
    activities.forEach(activity => decorateTrigger(activity.trigger, false));
    activities.clear();
    showTimer = null;
    hideTimer = null;
    conceal();
  }

  return {begin, end, reset};
})();

function najaLoadingMessage(trigger) {
  const element = triggerElementForMessage(trigger);
  const form = element instanceof HTMLFormElement ? element : element?.form;
  return form ? 'Zpracovávám požadavek…' : 'Načítám data…';
}

function triggerElementForMessage(value) {
  return value instanceof Element ? value : null;
}

function initializeLoadingFeedback() {
  naja.uiHandler.addEventListener('interaction', event => {
    const detail = event.detail;
    const originalEvent = detail.originalEvent;
    detail.options.fidoLoadingTrigger = originalEvent?.submitter || detail.element;
  });

  naja.addEventListener('start', event => {
    const options = event.detail.options;
    if (options.fidoLoadingToken) return;
    const trigger = options.fidoLoadingTrigger || null;
    options.fidoLoadingToken = loadingFeedback.begin({
      trigger,
      message: najaLoadingMessage(trigger),
    });
  });

  naja.addEventListener('complete', event => {
    const options = event.detail.options;
    loadingFeedback.end(options.fidoLoadingToken);
    delete options.fidoLoadingToken;
  });

  window.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : null;
    const anchor = target?.closest('a[href]');
    if (!anchor || anchor.classList.contains('ajax') || anchor.hasAttribute('download') || anchor.hasAttribute('data-fido-no-loading')) return;
    if (anchor.matches('[data-bs-toggle], [data-bs-dismiss], [data-toggle], [data-dismiss]')) return;
    const targetName = (anchor.getAttribute('target') || '').toLowerCase();
    if (targetName && targetName !== '_self') return;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (_) {
      return;
    }
    if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin) return;
    const current = new URL(window.location.href);
    if (url.pathname === current.pathname && url.search === current.search && url.hash) return;
    loadingFeedback.begin({trigger: anchor, message: 'Načítám stránku…', immediate: true});
  });

  window.addEventListener('submit', event => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.classList.contains('ajax') || form.hasAttribute('data-fido-no-loading')) return;
    if (event.submitter?.classList.contains('ajax')) return;
    const targetName = (form.getAttribute('target') || '').toLowerCase();
    if (targetName && targetName !== '_self') return;
    loadingFeedback.begin({trigger: event.submitter || form, message: 'Zpracovávám požadavek…', immediate: true});
  });

  window.addEventListener('beforeunload', () => {
    if (document.body?.dataset.fidoLoading !== 'true') {
      loadingFeedback.begin({message: 'Načítám stránku…', immediate: true});
    }
  });
  window.addEventListener('pageshow', () => loadingFeedback.reset());
}

const legacyDataAttributes = {
  'data-toggle': 'data-bs-toggle',
  'data-target': 'data-bs-target',
  'data-dismiss': 'data-bs-dismiss',
  'data-parent': 'data-bs-parent',
  'data-container': 'data-bs-container',
  'data-placement': 'data-bs-placement',
  'data-trigger': 'data-bs-trigger',
  'data-content': 'data-bs-content',
  'data-html': 'data-bs-html',
  'data-original-title': 'data-bs-title',
};

function upgradeLegacyDataAttributes(root = document) {
  for (const [oldName, newName] of Object.entries(legacyDataAttributes)) {
    root.querySelectorAll(`[${oldName}]`).forEach((element) => {
      if (oldName === 'data-toggle' && element.getAttribute(oldName) === 'buttons') {
        element.setAttribute('data-fido-button-group', '');
        return;
      }
      if (!element.hasAttribute(newName)) {
        element.setAttribute(newName, element.getAttribute(oldName) ?? '');
      }
    });
  }
}

function syncButtonGroup(group) {
  group.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
    const label = input.closest('label');
    if (!label) return;
    label.classList.toggle('active', input.checked);
    label.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
  });
}

function prepareButtonGroup(group) {
  Array.from(group.querySelectorAll('.radio > label, .checkbox > label')).forEach((label) => {
    label.classList.add('btn', 'btn-default', 'btn-edit-radio');
    const input = label.querySelector('input[type="radio"], input[type="checkbox"]');
    if (input) input.autocomplete = 'off';
    const wrapper = label.parentElement;
    if (wrapper?.parentElement === group) wrapper.replaceWith(label);
  });
}

function initializeButtonGroups(root = document) {
  root.querySelectorAll('[data-fido-button-group]').forEach((group) => {
    prepareButtonGroup(group);
    syncButtonGroup(group);
    if (group.dataset.fidoButtonGroupReady === '1') return;
    group.dataset.fidoButtonGroupReady = '1';
    group.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"], input[type="checkbox"]')) return;
      syncButtonGroup(group);
    });
  });
}

function initializeBootstrap(root = document) {
  upgradeLegacyDataAttributes(root);
  initializeButtonGroups(root);
  root.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
    bootstrap.Tooltip.getOrCreateInstance(element, {html: true});
  });
  root.querySelectorAll('[data-bs-toggle="popover"]').forEach((element) => {
    bootstrap.Popover.getOrCreateInstance(element, {html: true});
  });
}

function resolveElement(target) {
  return typeof target === 'string' ? document.querySelector(target) : target;
}

function showModal(target, options = {}) {
  const element = resolveElement(target);
  if (!element) return null;
  const modal = bootstrap.Modal.getOrCreateInstance(element, options);
  modal.show();
  return modal;
}

function hideModal(target) {
  const element = resolveElement(target);
  if (!element) return;
  bootstrap.Modal.getInstance(element)?.hide();
}

function hideAllModals() {
  document.querySelectorAll('.modal.show').forEach((element) => hideModal(element));
}

window.FidoFrontend = {
  hideAllModals,
  hideModal,
  initializeButtonGroups,
  initializeBootstrap,
  loading: loadingFeedback,
  showModal,
  upgradeLegacyDataAttributes,
};

document.addEventListener('DOMContentLoaded', () => {
  initializeBootstrap();

  naja.formsHandler.netteForms = netteForms;
  initializeLoadingFeedback();
  initializeDatepickerViewportGuard();
  naja.initialize();

  createDatagrids(new NajaAjax(naja), {
    datagrid: {
      plugins: [
        new AutosubmitPlugin(),
        new CheckboxPlugin(),
        new ConfirmPlugin(),
        new EditablePlugin(),
        new InlinePlugin(),
        new ItemDetailPlugin(),
        new NetteFormsPlugin(netteForms),
        new SortablePlugin(new SortableJS()),
        new DatepickerPlugin(new VanillaDatepicker(datagridDatepickerOptions)),
        new SelectpickerPlugin(new TomSelect(Select)),
        new TreeViewPlugin(),
      ],
    },
  });
}, {once: true});
